'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import LoadingSkeleton from './loading-skeleton';

const Chart = dynamic(() => import('react-apexcharts'), { ssr: false });

interface VesselConditionChartProps {
  data: {
    sail: number;
    running: number;
    ongoing?: number;
    maneuver?: number;
    stopped?: number;
  };
}

export default function VesselConditionChart({ data }: VesselConditionChartProps) {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  const hasAdvanced = Boolean(
    data && (data.ongoing !== undefined || data.maneuver !== undefined || data.stopped !== undefined)
  );

  const ongoing = hasAdvanced ? (data.ongoing ?? 0) : (data?.sail ?? 0);
  const maneuver = hasAdvanced ? (data.maneuver ?? 0) : 0;
  const stopped = hasAdvanced ? (data.stopped ?? 0) : (data?.running ?? 0);

  const vesselConditions = hasAdvanced
    ? [
        { status: 'Ongoing', count: ongoing, color: '#10B981' }, // Emerald
        { status: 'Maneuver', count: maneuver, color: '#F59E0B' }, // Amber
        { status: 'Stopped', count: stopped, color: '#64748B' }, // Slate
      ]
    : [
        { status: 'Sailing', count: data?.sail ?? 0, color: '#0096FB' },
        { status: 'Running', count: data?.running ?? 0, color: '#06B6D4' },
      ];

  const chartOptions = {
    chart: {
      type: 'donut' as const,
      animations: {
        enabled: true,
        easing: 'easeinout' as const,
        speed: 1000,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
      },
    },
    colors: vesselConditions.map((v) => v.color),
    labels: vesselConditions.map((v) => v.status),
    legend: {
      position: 'bottom' as const,
      fontSize: '12px',
      fontFamily: 'Inter, sans-serif',
      formatter: (seriesName: string, opts: { seriesIndex: number; w: { globals: { series: number[] } } }) => {
        const val = opts.w.globals.series[opts.seriesIndex];
        return `${seriesName}: ${val}`;
      },
    },
    stroke: {
      show: false,
    },
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '12px',
              fontFamily: 'Poppins, sans-serif',
              color: '#94a3b8',
            },
            value: {
              show: true,
              fontSize: '20px',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 'bold',
              color: '#021C32',
            },
            total: {
              show: true,
              showAlways: true,
              label: 'Total Vessels',
              color: '#94a3b8',
              formatter: () => {
                const total = vesselConditions.reduce((acc, curr) => acc + curr.count, 0);
                return `${total}`;
              },
            },
          },
        },
        expandOnClick: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      theme: 'light' as const,
      y: {
        formatter: (val: number) => val + ' vessels',
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 260,
          },
          legend: {
            position: 'bottom' as const,
          },
        },
      },
    ],
  };

  const chartSeries = vesselConditions.map((v) => v.count);

  if (isLoading) {
    return <LoadingSkeleton className="h-[250px] w-full" />;
  }

  return <Chart options={chartOptions} series={chartSeries} type="donut" height={250} />;
}
